import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarPlus, Bell, History, CalendarDays, Mail, Link2, Pencil, Trash2, Clock, CheckCircle2, Activity, CalendarCheck } from "lucide-react";
import { api, apiErr } from "../api";
import { FullLoader, Empty, StatusPill, prettyDate, STATUS_LABEL, WhatsAppIcon } from "../ui";
import AppointmentModal from "../components/AppointmentModal";
import NextAppointmentModal from "../components/NextAppointmentModal";
import { handleSendResult, copyText } from "../components/SendResult";
import { BRAND } from "../brand";

const VIEWS = [
  { id: "proximas", label: "Próximas", icon: CalendarDays },
  { id: "todas", label: "Todas las citas", icon: CalendarCheck },
  { id: "historial", label: "Historial de envíos", icon: History },
];

export default function AgendaTab({ patients, reloadPatients }) {
  const [view, setView] = useState("proximas");
  const [agenda, setAgenda] = useState(null);
  const [all, setAll] = useState([]);
  const [logs, setLogs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [nextFor, setNextFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, b, h] = await Promise.all([api.get("/agenda"), api.get("/appointments"), api.get("/reminders/history")]);
      setAgenda(a.data); setAll(b.data); setLogs(h.data);
    } catch (e) { toast.error(apiErr(e, "No se pudo cargar la agenda")); setAgenda((x) => x || []); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const thisWeek = useMemo(() => (agenda || []).filter((r) => r.reminder_due_this_week), [agenda]);
  const pending = (agenda || []).filter((r) => r.status === "reprogramar");
  if (!agenda) return <FullLoader text="Cargando agenda…" />;

  const refresh = () => { load(); reloadPatients && reloadPatients(); };
  const setStatus = async (a, status) => {
    try { await api.put(`/appointments/${a.id}/status`, { status }); toast.success(`Cita: ${STATUS_LABEL[status]}`); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (a) => {
    if (!window.confirm(`¿Eliminar la cita de ${a.nombre}?`)) return;
    try { await api.delete(`/appointments/${a.id}`); toast.success("Cita eliminada"); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const send = async (a, channel) => {
    try { const { data } = await api.post(`/appointments/${a.id}/notify`, { channel }); handleSendResult(data, channel); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const confirmLink = (a) => `${window.location.origin}/cita/${a.confirm_token}`;

  const rows = view === "proximas" ? agenda : [...all].sort((x, y) => (y.fecha + (y.hora || "")).localeCompare(x.fecha + (x.hora || "")));

  return (
    <div>
      <div className="grid lg:grid-cols-2 gap-3 mb-6">
        <div className="pn-card rounded-2xl p-4">
          <div className="flex items-center gap-2 font-semibold pn-brand mb-2"><Bell size={17} /> Recordatorios automáticos esta semana</div>
          {thisWeek.length === 0 ? <p className="text-sm pn-sub">No hay recordatorios programados para los próximos 7 días.</p> : (
            <div className="flex flex-wrap gap-1.5">{thisWeek.map((r) => (
              <span key={r.id} className="text-xs pn-card2 border pn-line rounded-full px-2.5 py-1 pn-ink"><b>{r.nombre}</b> · sale el {prettyDate(r.reminder_date)}</span>
            ))}</div>
          )}
          <p className="text-[11px] pn-muted mt-2">Se envían solos por email {BRAND.reminderRule}</p>
        </div>
        <div className="pn-card rounded-2xl p-4">
          <div className="flex items-center gap-2 font-semibold mb-2" style={{ color: "var(--warn)" }}><Clock size={17} /> Pacientes que piden reprogramar</div>
          {pending.length === 0 ? <p className="text-sm pn-sub">Nadie ha pedido cambiar su cita.</p> : (
            <div className="space-y-1.5">{pending.map((r) => (
              <div key={r.id} className="text-sm pn-ink flex flex-wrap items-center gap-2">
                <b>{r.nombre}</b> <span className="pn-sub">pide {r.requested_fecha || "otra fecha"}{r.nota_paciente ? ` — “${r.nota_paciente}”` : ""}</span>
                <button onClick={() => setEditing(r)} className="pn-accent text-xs font-semibold">Cambiar fecha →</button>
              </div>
            ))}</div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        {VIEWS.map((v) => (
          <button key={v.id} onClick={() => setView(v.id)} className={`pn-btn !py-2 ${view === v.id ? "pn-btn-soft" : "pn-btn-ghost"}`}>
            <v.icon size={15} /> {v.label} <span className="pn-muted text-xs">({v.id === "proximas" ? agenda.length : v.id === "todas" ? all.length : logs.length})</span>
          </button>
        ))}
        <button onClick={() => setEditing({})} className="pn-btn pn-btn-primary ml-auto"><CalendarPlus size={17} /> Nueva cita</button>
      </div>

      {view === "historial" ? (
        logs.length === 0 ? <Empty icon={History}>Aún no hay envíos registrados.</Empty> : (
          <div className="pn-card rounded-2xl overflow-x-auto">
            <table className="pn-table min-w-[760px]">
              <thead><tr><th>Fecha</th><th>Paciente</th><th>Tipo</th><th>Canal</th><th>Estado</th><th>Cita</th></tr></thead>
              <tbody>{logs.map((l) => (
                <tr key={l.id}>
                  <td className="pn-sub whitespace-nowrap">{new Date(l.created_at).toLocaleString("es-EC", { dateStyle: "medium", timeStyle: "short" })}</td>
                  <td className="font-medium pn-ink">{l.nombre}</td>
                  <td className="pn-sub capitalize">{l.kind}</td>
                  <td className="pn-sub capitalize">{l.channel}</td>
                  <td><StatusPill status={l.status} />{l.status === "fallido" && l.detail && <div className="text-[10px] pn-muted mt-0.5 max-w-[220px] truncate" title={l.detail}>{l.detail}</div>}</td>
                  <td className="pn-sub">{prettyDate(l.fecha_cita)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )
      ) : rows.length === 0 ? (
        <Empty icon={CalendarDays}>{view === "proximas" ? "No hay citas próximas." : "Aún no hay citas."} Usa “Nueva cita” o “Próxima consulta” desde un paciente.</Empty>
      ) : (
        <div className="pn-card rounded-2xl overflow-x-auto">
          <table className="pn-table min-w-[980px]">
            <thead><tr><th>Fecha</th><th>Paciente</th><th>Servicio</th><th>Estado</th><th>Recordatorio</th><th className="text-right">Acciones</th></tr></thead>
            <tbody>{rows.map((a) => (
              <tr key={a.id} className="pn-row-hover">
                <td className="whitespace-nowrap">
                  <div className="font-semibold pn-ink">{prettyDate(a.fecha, true)}</div>
                  <div className="text-xs pn-sub flex items-center gap-1"><Clock size={11} /> {a.hora || "sin hora"}{a.dias_para_cita != null && a.dias_para_cita >= 0 && <span className="pn-muted"> · en {a.dias_para_cita} d</span>}</div>
                </td>
                <td>
                  {a.patient_id ? <Link to={`/pacientes/${a.patient_id}`} className="font-medium pn-ink hover:underline">{a.nombre}</Link> : <span className="font-medium pn-ink">{a.nombre}</span>}
                  {a.chronic && <span className="ml-1.5 text-[11px] pn-brand"><Activity size={10} className="inline" /> crónico</span>}
                  <div className="text-xs pn-muted">{a.telefono || a.email || (a.patient_id ? "" : "no registrado")}</div>
                </td>
                <td className="pn-sub text-sm">{a.servicio || "Consulta"}{a.nota && <div className="text-xs pn-muted max-w-[200px] truncate" title={a.nota}>{a.nota}</div>}</td>
                <td>
                  {a.virtual ? <StatusPill status="previsto" /> : (
                    <select value={a.status} onChange={(e) => setStatus(a, e.target.value)} className={`pn-pill pn-st-${a.status} cursor-pointer outline-none`} aria-label="Estado de la cita">
                      {["pendiente", "confirmada", "reprogramar", "cancelada", "atendida"].map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                    </select>
                  )}
                  {a.status === "reprogramar" && a.requested_fecha && <div className="text-[11px] mt-0.5" style={{ color: "var(--warn)" }}>pide: {a.requested_fecha}</div>}
                </td>
                <td className="text-xs pn-sub whitespace-nowrap">
                  {a.reminder_sent ? <span className="inline-flex items-center gap-1" style={{ color: "var(--ok)" }}><CheckCircle2 size={13} /> enviado</span>
                    : a.reminder_eligible && a.reminder_date < new Date().toISOString().slice(0, 10) ? <span className="pn-muted" title="La fecha del recordatorio ya pasó; envíalo manualmente si hace falta">fecha pasada</span>
                    : a.reminder_eligible ? <>{prettyDate(a.reminder_date)}{a.reminder_due_this_week && <span className="ml-1 pn-pill pn-soft-brand">esta semana</span>}</>
                    : a.reminder_date ? <span className="pn-muted" title="Solo se recuerdan citas a 30+ días de la atención o de pacientes crónicos">no aplica</span> : "—"}
                </td>
                <td>
                  <div className="flex items-center justify-end gap-1.5">
                    {a.virtual ? (
                      <button onClick={() => setNextFor(patients.find((p) => p.id === a.patient_id))} className="pn-btn pn-btn-soft !px-2.5 !py-1.5 text-xs"><CalendarPlus size={14} /> Agendar</button>
                    ) : (<>
                      <button onClick={() => send(a, "whatsapp")} className="pn-icon-btn" title="Enviar por WhatsApp" style={{ color: "#16A34A" }}><WhatsAppIcon /></button>
                      <button onClick={() => send(a, "email")} className="pn-icon-btn" title={a.email ? "Enviar por email" : "Sin email"} disabled={!a.email}><Mail size={15} /></button>
                      <button onClick={() => copyText(confirmLink(a), "Enlace de confirmación copiado")} className="pn-icon-btn" title="Copiar enlace de confirmación"><Link2 size={15} /></button>
                      <button onClick={() => setEditing(a)} className="pn-icon-btn" title="Editar / mover"><Pencil size={15} /></button>
                      <button onClick={() => del(a)} className="pn-icon-btn" title="Eliminar"><Trash2 size={15} /></button>
                    </>)}
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {editing && <AppointmentModal initial={editing.id ? editing : null} patients={patients} taken={all} onClose={() => setEditing(null)} onSaved={refresh} />}
      {nextFor && <NextAppointmentModal patient={nextFor} onClose={() => setNextFor(null)} onSaved={refresh} />}
    </div>
  );
}
